import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Code, Copy, Eye, FileText, Hash, Loader2, MoreHorizontal, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTemplateListQuery } from '@/client/api/campaigns/campaigns.query';
import { useDeleteTemplateMutation } from '@/client/api/campaigns/campaigns.mutation';
import { NotificationTemplate } from '@/client/types/campaigns';
import SortableTh from '@/client/components/common/SortableTh';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  EditorState,
  TemplateEditorDialog,
  TemplatePreviewDialog,
  copyText,
  isDesignedHtml,
} from './TemplateDialogs';

type SortField = 'ntmplid' | 'name' | 'name_system' | 'locale' | 'date_modified';
type ContentFilter = 'all' | 'designed' | 'email' | 'sms' | 'empty';
type ProviderFilter = 'all' | 'brevo' | 'ses' | 'none';

const updatedAt = (t: NotificationTemplate) => String(t.date_modified ?? t.date_added ?? '');
const fmtDate = (s: string) =>
  s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const select =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500';

function Pill({ children, tone = 'gray' }: { children: React.ReactNode; tone?: 'gray' | 'violet' | 'blue' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    violet: 'bg-violet-50 text-violet-700',
    blue: 'bg-blue-50 text-blue-700',
  };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
}

export default function CampaignTemplatesPanel({ canWrite }: { canWrite: boolean }) {
  const [search, setSearch] = useState('');
  const [content, setContent] = useState<ContentFilter>('all');
  const [provider, setProvider] = useState<ProviderFilter>('all');
  const [locale, setLocale] = useState('all');
  const [sortBy, setSortBy] = useState<SortField>('date_modified');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [preview, setPreview] = useState<NotificationTemplate | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [toDelete, setToDelete] = useState<NotificationTemplate | null>(null);

  // The API returns every template (a few hundred rows), so search, filters and
  // sorting run here rather than as server params.
  const { data, isLoading, isError, isFetching } = useTemplateListQuery({});
  const del = useDeleteTemplateMutation();
  const all = data?.templates ?? [];

  const locales = useMemo(() => [...new Set(all.map((t) => t.locale || 'en_GB'))].sort(), [all]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = all.filter((t) => {
      if (q && ![t.name_system, t.name, t.email_text, t.sms_text, String(t.ntmplid)].some((v) => String(v ?? '').toLowerCase().includes(q)))
        return false;
      // Brevo/SES templates hold the email body themselves, so they count as email.
      const hasEmail = !!(t.email_html || t.email_text || t.brevo_template_id || t.ses_template_id);
      if (content === 'designed' && !isDesignedHtml(t.email_html)) return false;
      if (content === 'email' && !hasEmail) return false;
      if (content === 'sms' && !t.sms_text) return false;
      if (content === 'empty' && (hasEmail || t.sms_text)) return false;
      if (provider === 'brevo' && !t.brevo_template_id) return false;
      if (provider === 'ses' && !t.ses_template_id) return false;
      if (provider === 'none' && (t.brevo_template_id || t.ses_template_id)) return false;
      if (locale !== 'all' && (t.locale || 'en_GB') !== locale) return false;
      return true;
    });
    const key = (t: NotificationTemplate) =>
      sortBy === 'ntmplid' ? t.ntmplid : sortBy === 'date_modified' ? updatedAt(t) : String(t[sortBy] ?? '').toLowerCase();
    rows.sort((a, b) => {
      const x = key(a);
      const y = key(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return sortOrder === 'asc' ? c : -c;
    });
    return rows;
  }, [all, search, content, provider, locale, sortBy, sortOrder]);

  const pageRows = filtered.slice((page - 1) * limit, page * limit);
  const filtersOn = !!search || content !== 'all' || provider !== 'all' || locale !== 'all';

  const toggleSort = (field: string) => {
    if (sortBy === field) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(field as SortField);
      setSortOrder(field === 'date_modified' || field === 'ntmplid' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setContent('all');
    setProvider('all');
    setLocale('all');
    setPage(1);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await del.mutateAsync({ ntmplid: toDelete.ntmplid, idempotencyKey: generateIdempotencyKey() });
      toast.success(`Template #${toDelete.ntmplid} deleted`, { richColors: true });
      setToDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Delete failed', { richColors: true });
    }
  };

  const openEditor = (s: EditorState) => {
    setPreview(null);
    setEditor(s);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, system name, copy or #id…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          aria-label="Content"
          value={content}
          onChange={(e) => {
            setContent(e.target.value as ContentFilter);
            setPage(1);
          }}
          className={select}
        >
          <option value="all">All content</option>
          <option value="designed">Designed email</option>
          <option value="email">Has email</option>
          <option value="sms">Has SMS</option>
          <option value="empty">No copy</option>
        </select>
        <select
          aria-label="Provider"
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value as ProviderFilter);
            setPage(1);
          }}
          className={select}
        >
          <option value="all">All providers</option>
          <option value="brevo">Brevo</option>
          <option value="ses">SES</option>
          <option value="none">No provider template</option>
        </select>
        <select
          aria-label="Locale"
          value={locale}
          onChange={(e) => {
            setLocale(e.target.value);
            setPage(1);
          }}
          className={select}
        >
          <option value="all">All locales</option>
          {locales.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        {filtersOn && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
        <Link
          to="/admin/communications/notifications/providers"
          className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          Provider templates
        </Link>
        {canWrite && (
          <Button type="button" onClick={() => openEditor({ mode: 'create' })} className="gap-2">
            <Plus className="h-4 w-4" />
            New template
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 text-xs text-gray-500">
          <span>
            {isLoading
              ? 'Loading templates…'
              : filtersOn
                ? `${filtered.length} of ${all.length} templates`
                : `${all.length} templates`}
          </span>
          {isFetching && !isLoading && (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Refreshing
            </span>
          )}
        </div>

        {isLoading && (
          <div className="space-y-3 p-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}
        {isError && <p className="p-8 text-center text-sm text-red-600">Failed to load templates.</p>}
        {!isLoading && !isError && filtered.length === 0 && (
          <div className="space-y-3 p-10 text-center">
            <p className="text-sm text-gray-500">{filtersOn ? 'No templates match these filters.' : 'No templates yet.'}</p>
            {filtersOn ? (
              <Button variant="outline" onClick={resetFilters}>
                Clear filters
              </Button>
            ) : (
              canWrite && <Button onClick={() => openEditor({ mode: 'create' })}>Create the first template</Button>
            )}
          </div>
        )}

        {!isLoading && pageRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <SortableTh label="#" field="ntmplid" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} className="w-16 !px-4" />
                  <SortableTh label="Template" field="name" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} className="!px-4" />
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Channels</th>
                  <SortableTh label="Locale" field="locale" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} className="!px-4" />
                  <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Provider</th>
                  <SortableTh label="Updated" field="date_modified" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} className="!px-4" />
                  <th className="w-12 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((t) => {
                  const designed = isDesignedHtml(t.email_html);
                  const ownEmail = !!(t.email_html || t.email_text);
                  const providerEmail = !ownEmail && !!(t.brevo_template_id || t.ses_template_id);
                  const hasEmail = ownEmail || providerEmail;
                  return (
                    <tr key={t.ntmplid} onClick={() => setPreview(t)} className="cursor-pointer hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{t.ntmplid}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{t.name || t.name_system}</div>
                        <div className="font-mono text-[11px] text-gray-500">{t.name_system}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {designed ? (
                            <Pill tone="violet">Designed email</Pill>
                          ) : providerEmail ? (
                            <Pill tone="blue">Email in {t.brevo_template_id ? 'Brevo' : 'SES'}</Pill>
                          ) : (
                            ownEmail && <Pill tone="blue">Email</Pill>
                          )}
                          {t.sms_text && <Pill>SMS</Pill>}
                          {!hasEmail && !t.sms_text && <span className="text-xs text-gray-400">No copy</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.locale || 'en_GB'}</td>
                      <td className="px-4 py-3 font-mono text-[11px] text-gray-600">
                        {[t.brevo_template_id && `Brevo ${t.brevo_template_id}`, t.ses_template_id && `SES ${t.ses_template_id}`]
                          .filter(Boolean)
                          .join(' · ') || <span className="font-sans text-xs text-gray-400">None</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{fmtDate(updatedAt(t))}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Actions for template ${t.ntmplid}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => setPreview(t)} className="gap-2">
                              <Eye className="h-4 w-4" />
                              Preview
                            </DropdownMenuItem>
                            {canWrite && (
                              <>
                                <DropdownMenuItem onClick={() => openEditor({ mode: 'edit', template: t })} className="gap-2">
                                  <Pencil className="h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditor({ mode: 'duplicate', template: t })} className="gap-2">
                                  <Copy className="h-4 w-4" />
                                  Duplicate
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={!t.email_html}
                              onClick={() => copyText(t.email_html ?? '', 'HTML')}
                              className="gap-2"
                            >
                              <Code className="h-4 w-4" />
                              Copy HTML
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={!t.email_text}
                              onClick={() => copyText(t.email_text ?? '', 'Plain text')}
                              className="gap-2"
                            >
                              <FileText className="h-4 w-4" />
                              Copy plain text
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => copyText(t.name_system, 'System name')} className="gap-2">
                              <Hash className="h-4 w-4" />
                              Copy system name
                            </DropdownMenuItem>
                            {canWrite && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setToDelete(t)}
                                  className="gap-2 text-red-600 focus:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > limit && (
          <div className="border-t border-gray-100 px-4 py-3">
            <Pagination
              total={filtered.length}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={setPage}
              setItemsPerPage={(n: number) => {
                setLimit(n);
                setPage(1);
              }}
              showButtons
              showSelect
            />
          </div>
        )}
      </div>

      <TemplatePreviewDialog
        template={preview}
        canWrite={canWrite}
        onClose={() => setPreview(null)}
        onEdit={(t) => openEditor({ mode: 'edit', template: t })}
        onDuplicate={(t) => openEditor({ mode: 'duplicate', template: t })}
      />
      <TemplateEditorDialog state={editor} onClose={() => setEditor(null)} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template #{toDelete?.ntmplid}?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.name || toDelete?.name_system} will be removed. Notifications and campaigns that use{' '}
              <span className="font-mono">{toDelete?.name_system}</span> will have no copy to send. This can&rsquo;t
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={del.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
